// Redux slice for managing API key related state and async actions
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
// Import API service functions for API key operations
import { _getApiKeys, _createApiKey, _disableApiKey, _enableApiKey, _deleteApiKey } from "../shared/httpservice/apis/apiKey";
// Helper to create an initial state for a given type
const createInitialState = () => ({
    loading: false,
    response: null,
    hasError: false,
    error: {},
});
// The initial state for the API key slice
const initialState = {
    apiKey: createInitialState(),
    createApiKey: createInitialState(), // State for creating API keys
};
// Async thunk to fetch all API keys for a site
export const getApiKeysAction = createAsyncThunk("apiKey/getApiKeys", async (siteId, { rejectWithValue }) => {
    try {
        const response = await _getApiKeys(siteId);
        return response;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
// Async thunk to create a new API key
export const createApiKeyAction = createAsyncThunk("apiKey/create", async (data, { rejectWithValue }) => {
    try {
        const response = await _createApiKey(data);
        return response;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
// Async thunk to disable an API key
export const disableApiKeyAction = createAsyncThunk("apiKey/disable", async ({ siteId, apiKeyId }, { rejectWithValue }) => {
    try {
        const response = await _disableApiKey(siteId, apiKeyId);
        return response;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
// Async thunk to enable an API key
export const enableApiKeyAction = createAsyncThunk("apiKey/enable", async ({ siteId, apiKeyId }, { rejectWithValue }) => {
    try {
        const response = await _enableApiKey(siteId, apiKeyId);
        return response;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
// Async thunk to delete an API key
export const deleteApiKeyAction = createAsyncThunk("apiKey/delete", async ({ siteId, apiKeyId }, { rejectWithValue }) => {
    try {
        const response = await _deleteApiKey(siteId, apiKeyId);
        return response;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
// The main slice for API key state and reducers
const apiKeySlice = createSlice({
    name: 'apiKey',
    initialState,
    reducers: {
        // Resets the entire API key slice state to initial
        resetApiKeySlice: () => initialState,
    },
    extraReducers: (builder) => {
        // Handle getApiKeysAction lifecycle
        builder.addCase(getApiKeysAction.pending, (state) => {
            state.apiKey.loading = true;
            state.apiKey.response = null;
            state.apiKey.hasError = false;
            state.apiKey.error = {};
        })
            .addCase(getApiKeysAction.fulfilled, (state, action) => {
            state.apiKey.loading = false;
            state.apiKey.response = action.payload;
            state.apiKey.hasError = false;
            state.apiKey.error = {};
        })
            .addCase(getApiKeysAction.rejected, (state, action) => {
            state.apiKey.loading = false;
            state.apiKey.response = null;
            state.apiKey.hasError = true;
            state.apiKey.error = action.payload;
        });
        // Handle createApiKeyAction lifecycle
        builder.addCase(createApiKeyAction.pending, (state) => {
            state.createApiKey.loading = true;
            state.createApiKey.response = null;
            state.createApiKey.hasError = false;
            state.createApiKey.error = {};
        })
            .addCase(createApiKeyAction.fulfilled, (state, action) => {
            state.createApiKey.loading = false;
            state.createApiKey.response = action.payload;
            state.createApiKey.hasError = false;
            state.createApiKey.error = {};
        })
            .addCase(createApiKeyAction.rejected, (state, action) => {
            state.createApiKey.loading = false;
            state.createApiKey.response = null;
            state.createApiKey.hasError = true;
            state.createApiKey.error = action.payload;
        });
    }
});
// Export the reducer and actions
export default apiKeySlice.reducer;
export const { resetApiKeySlice } = apiKeySlice.actions;
